/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Sector experience pickers laid out for tablet and desktop breakpoints.
 * @module components/AppointmentRegistrationSections/Certifications/SectorExperienceGrid
 */

import React from "react";
import { chunkArray } from "../../../utils/appointmentRegistrationUtils.js";
import { selectStyles } from "../../../utils/common";
import {
  SECTOR_EXPERIENCE_DESKTOP_COLUMNS,
  SECTOR_EXPERIENCE_DESKTOP_SELECT_STYLES,
  SECTOR_EXPERIENCE_TABLET_COLUMNS,
} from "../../../utils/certificationSectionUtils.js";
import SectorExperienceSelect from "./SectorExperienceSelect.jsx";

/** Builds a stable React key for a row of sector experience controls. */
const buildSectorExperienceRowKey = (rowSectors) =>
  rowSectors.map((sector) => sector.value).join("-");

/**
 * @param {Object} props
 * @param {Array} props.sectors - Selected sectors requiring experience values
 * @param {Array} props.yearsOptions - react-select options for years of experience
 * @param {boolean} props.isTablet - When true, render compact two-column rows
 * @param {Function} props.onExperienceChange - `(sectorValue, selectedOption) => void`
 * @param {Function} props.t - i18n translate function
 * @param {Object} props.styles - CSS module class map
 */
const SectorExperienceGrid = ({
  sectors,
  yearsOptions,
  isTablet,
  onExperienceChange,
  t,
  styles,
}) => {
  if (sectors.length === 0) {
    return null;
  }

  const sectorsPerRow = isTablet
    ? SECTOR_EXPERIENCE_TABLET_COLUMNS
    : SECTOR_EXPERIENCE_DESKTOP_COLUMNS;
  const experiencePlaceholder = t(
    "components.certifications.yearsOfExperienceInTheSectorPlaceholder",
  );

  return chunkArray(sectors, sectorsPerRow).map((rowSectors) => {
    const rowKey = buildSectorExperienceRowKey(rowSectors);

    if (isTablet) {
      return (
        <div key={rowKey} className={styles.inputRow}>
          {rowSectors.map((sector) => (
            <div key={`select-${sector.value}`} className={styles.inputGroup}>
              <label className={styles.inputLabel}>
                {t(`components.certifications.sectorOption${sector.value + 1}`)}
              </label>
              <SectorExperienceSelect
                sector={sector}
                yearsOptions={yearsOptions}
                placeholder={experiencePlaceholder}
                selectStyles={selectStyles}
                onExperienceChange={onExperienceChange}
              />
            </div>
          ))}
        </div>
      );
    }

    return (
      <React.Fragment key={rowKey}>
        <div className={styles.checkboxRow}>
          {rowSectors.map((sector) => (
            <div key={`label-${sector.value}`} className={styles.checkboxItem}>
              <label
                className={`${styles.checkboxLabel} ${styles.specialCheckboxLabel}`}
              >
                {t(`components.certifications.sectorOption${sector.value + 1}`)}
              </label>
            </div>
          ))}
        </div>
        <div className={styles.checkboxRow}>
          {rowSectors.map((sector) => (
            <div key={`select-${sector.value}`} className={styles.checkboxItem}>
              <SectorExperienceSelect
                sector={sector}
                yearsOptions={yearsOptions}
                placeholder={experiencePlaceholder}
                selectStyles={SECTOR_EXPERIENCE_DESKTOP_SELECT_STYLES}
                onExperienceChange={onExperienceChange}
              />
            </div>
          ))}
        </div>
      </React.Fragment>
    );
  });
};

export default SectorExperienceGrid;
